import { useTranslation } from "react-i18next";
import type { VacancyStats } from "../../lib/realestate";

interface VacancyStatsCardProps {
  stats: VacancyStats | undefined;
  year: number;
  availableYears: number[];
  onYearChange: (year: number) => void;
}

export function VacancyStatsCard({
  stats,
  year,
  availableYears,
  onYearChange,
}: VacancyStatsCardProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-white border rounded p-4 space-y-3">
      <header className="flex justify-between items-center">
        <h3 className="font-semibold">{t("realestate.vacancy.stats_title")}</h3>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <span>{t("realestate.vacancy.stats_year_label")}</span>
          <select
            className="border rounded px-2 py-0.5 text-sm"
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </header>

      {stats && stats.perUnit.length > 0 ? (
        <table className="w-full text-sm">
          <tbody>
            {stats.perUnit.map((u) => {
              const vacantKey =
                u.vacantMonths === 1
                  ? "realestate.vacancy.stats_months_one"
                  : "realestate.vacancy.stats_months_other";
              const availableKey =
                u.availableMonths === 1
                  ? "realestate.vacancy.stats_months_one"
                  : "realestate.vacancy.stats_months_other";
              return (
                <tr key={u.unitLabel} className="border-b">
                  <td className="py-1">{u.unitLabel}</td>
                  <td className="py-1 text-right">
                    {t(vacantKey, { count: u.vacantMonths })} /{" "}
                    {t(availableKey, { count: u.availableMonths })}
                  </td>
                  <td className="py-1 text-right font-medium">
                    {t("realestate.vacancy.stats_rate", { rate: u.vacancyRatePct })}
                  </td>
                </tr>
              );
            })}
            <tr className="font-semibold">
              <td className="py-1">{t("realestate.vacancy.stats_total")}</td>
              <td className="py-1 text-right">
                {stats.totalVacantMonths} / {stats.totalAvailableMonths}
              </td>
              <td className="py-1 text-right">
                {t("realestate.vacancy.stats_rate", { rate: stats.totalVacancyRatePct })}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-slate-500">{t("realestate.vacancy.empty_hint")}</p>
      )}

      <p className="text-xs text-slate-400" title={t("realestate.vacancy.stats_tooltip")}>
        {t("realestate.vacancy.stats_tooltip")}
      </p>
    </div>
  );
}
